import { Quill } from "react-quill";

export const DESCRIPTION_MAX_LENGTH = 150;
export const DESCRIPTION_WARN_REMAINING = 5;

const ALLOWED_KEYS = new Set([
  "Backspace",
  "Delete",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
  "Tab",
  "Escape",
]);

class DescriptionMaxLength {
  constructor(quill, options) {
    this.quill = quill;
    this.max = options.max;

    quill.root.addEventListener("keydown", this.onKeyDown, true);
    quill.root.addEventListener("beforeinput", this.onBeforeInput, true);
    quill.root.addEventListener("paste", this.onPaste, true);
    quill.root.addEventListener("compositionend", this.onCompositionEnd);
    quill.on("text-change", this.onTextChange);
  }

  trimToMax() {
    const extra = this.currentLength() - this.max;
    if (extra > 0) {
      this.quill.deleteText(this.max, extra, "silent");
    }
  }

  currentLength() {
    return Math.max(0, this.quill.getLength() - 1);
  }

  selectedLength() {
    const range = this.quill.getSelection();
    return range?.length || 0;
  }

  remainingRoom() {
    return this.max - (this.currentLength() - this.selectedLength());
  }

  onKeyDown = (e) => {
    if (ALLOWED_KEYS.has(e.key) || e.ctrlKey || e.metaKey || e.altKey) {
      return;
    }
    if (e.isComposing || e.key === "Process") return;
    if (this.remainingRoom() > 0) return;
    if (e.key.length === 1 || e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  onBeforeInput = (e) => {
    if (!e.inputType || !e.inputType.startsWith("insert")) return;
    if (e.inputType === "insertFromPaste" || e.inputType === "insertFromDrop") {
      return;
    }
    const incoming = e.data ? e.data.length : 1;
    if (this.remainingRoom() >= incoming) return;
    e.preventDefault();
  };

  onPaste = (e) => {
    const pasted = e.clipboardData?.getData("text/plain") ?? "";
    const room = this.remainingRoom();
    if (room <= 0) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (pasted.length <= room) return;

    e.preventDefault();
    e.stopPropagation();
    const range =
      this.quill.getSelection(true) || { index: this.currentLength(), length: 0 };
    if (range.length) {
      this.quill.deleteText(range.index, range.length, "user");
    }
    const clipped = pasted.slice(0, room);
    this.quill.insertText(range.index, clipped, "user");
    this.quill.setSelection(range.index + clipped.length, 0);
  };

  onCompositionEnd = () => {
    this.trimToMax();
  };

  onTextChange = (delta, oldDelta, source) => {
    if (source === "silent") return;
    if (this.currentLength() <= this.max) return;
    this.quill.setContents(oldDelta, "silent");
  };
}

Quill.register("modules/maxlength", DescriptionMaxLength, true);

const descriptionPlainText = (html = "") => {
  if (typeof document === "undefined") {
    return String(html)
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.innerText || el.textContent || "").replace(/\n+$/g, "");
};

export const getDescriptionText = (html = "") =>
  descriptionPlainText(html).replace(/\s+/g, " ").trim();

export const getDescriptionLength = (html = "") =>
  descriptionPlainText(html).length;

export const descriptionQuillModules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
  ],
  maxlength: { max: DESCRIPTION_MAX_LENGTH },
};
